import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const ColorRow = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-3 py-2">
    <Label className="w-44 text-right text-sm">{label}</Label>
    <input
      type="color"
      value={value || '#000000'}
      onChange={(e) => onChange(e.target.value)}
      className="w-14 h-9 rounded border cursor-pointer"
      data-testid={testId}
    />
    <Input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-32 font-mono text-xs"
    />
  </div>
);

const AssetUploader = ({ kind, currentUrl, onUploaded, label, testId }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/pdf-settings/upload-asset?kind=${kind}`, fd, auth());
      toast.success(`✅ تم رفع ${label}`);
      onUploaded(r.data.url);
    } catch (e) {
      toast.error(`❌ فشل الرفع: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/pdf-settings/asset?kind=${kind}`, auth());
      onUploaded('');
      toast.success('✅ تمت الإزالة');
    } catch (e) {
      toast.error('❌ فشل الحذف');
    } finally {
      setBusy(false);
    }
  };

  const fullUrl = currentUrl?.startsWith('http')
    ? currentUrl
    : currentUrl
      ? `${API}${currentUrl}`
      : null;

  return (
    <div className="border rounded-xl p-4 bg-white shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-noir-900">{label}</h4>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-upload`}
          >
            📤 رفع
          </Button>
          {currentUrl && (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={handleRemove}
              data-testid={`${testId}-remove`}
            >
              🗑️ حذف
            </Button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div className="bg-gradient-to-br from-noir-50 to-brand-50 border border-dashed border-brand-300 rounded-lg flex items-center justify-center h-36">
        {fullUrl ? (
          <img src={fullUrl} alt={label} className="max-h-32 max-w-full object-contain" />
        ) : (
          <div className="text-noir-400 text-sm">لا توجد صورة بعد</div>
        )}
      </div>
    </div>
  );
};

const PDFSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingOpId, setTestingOpId] = useState('');
  const [previewBust, setPreviewBust] = useState(0); // bumps to force iframe reload
  const [livePreview, setLivePreview] = useState(true);
  const isUserEditing = useRef(false);   // becomes true on first user interaction
  const initialLoadDone = useRef(false); // skip the very first effect (initial fetch)

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pdf-settings`, auth());
        setSettings(r.data);
      } catch (e) {
        toast.error('فشل تحميل الإعدادات');
      }
      // grab any existing op for "preview" button
      try {
        const r = await axios.get(`${API}/daily-operations?page=1&page_size=1`, auth());
        if (r.data?.items?.[0]?.id) setTestingOpId(r.data.items[0].id);
      } catch (e) { /* silent */ }
      // Mark initial load DONE on the next tick so the first settings-change
      // effect (triggered by setSettings above) does NOT trigger a save.
      setTimeout(() => { initialLoadDone.current = true; }, 50);
    })();
  }, []);

  const update = (patch) => {
    isUserEditing.current = true;
    setSettings((s) => ({ ...s, ...patch }));
  };

  // Debounced auto-save + live preview reload. Only fires AFTER initial load
  // and AFTER the user touched something. Avoids the dreaded
  // "reload on mount → ERR_ABORTED" cycle that made the preview stay blank.
  useEffect(() => {
    if (!settings || !livePreview) return;
    if (!initialLoadDone.current || !isUserEditing.current) return;
    const handle = setTimeout(async () => {
      try {
        await axios.put(`${API}/pdf-settings`, settings, auth());
        setPreviewBust((n) => n + 1);
      } catch (e) {
        // silent — manual save still works
      }
    }, 1200);
    return () => clearTimeout(handle);
  }, [
    settings, livePreview,
  ]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/pdf-settings`, settings, auth());
      setSettings(r.data);
      isUserEditing.current = false;       // resync after manual save
      setPreviewBust((n) => n + 1);
      toast.success('✅ تم حفظ الإعدادات');
    } catch (e) {
      toast.error(`❌ فشل الحفظ: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const previewPdf = () => {
    if (!testingOpId) {
      toast.error('لا توجد عملية يومية لمعاينتها');
      return;
    }
    const t = localStorage.getItem('token');
    window.open(`${API}/daily-operations/${testingOpId}/print?token=${encodeURIComponent(t)}`, '_blank');
  };

  // Build URL for the live-preview iframe (with cache buster).
  const previewUrl = (() => {
    if (!testingOpId) return null;
    const t = localStorage.getItem('token');
    return `${API}/daily-operations/${testingOpId}/print?token=${encodeURIComponent(t)}&_=${previewBust}`;
  })();

  if (!settings) {
    return (
      <div className="p-8 text-center text-noir-500">
        جاري تحميل إعدادات PDF...
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl" data-testid="pdf-settings-page">
      {/* Header */}
      <div className="bg-gradient-to-l from-noir-900 via-noir-800 to-noir-900 text-brand-400 p-6 rounded-2xl shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-wide">🧾 إعدادات PDF</h1>
            <p className="text-brand-200 mt-1 text-sm">
              تحكّم كامل في تصميم وصولات وفواتير وكالات صنهاجة سفر — Royal Gold Edition
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const r = await axios.get(`${API}/pdf-audit-logs?limit=50`, auth());
                  const lines = (r.data.items || []).map((x) =>
                    `[${(x.at || '').slice(0, 16).replace('T', ' ')}] ${x.event} ${x.doc_type} ${x.doc_id} by ${x.user_email || '—'}`
                  ).join('\n');
                  alert(`📋 آخر 50 عملية PDF:\n\n${lines || '(لا يوجد)'}`);
                } catch (e) {
                  toast.error('فشل تحميل السجل');
                }
              }}
              className="bg-white text-noir-900 hover:bg-brand-100"
              data-testid="pdf-logs-btn"
            >
              📋 سجلّ التحميل والطباعة
            </Button>
            <Button
              variant="outline"
              onClick={previewPdf}
              className="bg-white text-noir-900 hover:bg-brand-100"
              data-testid="pdf-preview-btn"
            >
              👁️ معاينة وصل عيّنة
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-brand-500 hover:bg-brand-600 text-noir-900 font-bold"
              data-testid="pdf-save-btn"
            >
              {saving ? '⏳ جارٍ الحفظ...' : '💾 حفظ الإعدادات'}
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="identity" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-noir-100 p-2 h-auto">
          <TabsTrigger value="identity" data-testid="tab-identity">🏢 الهوية</TabsTrigger>
          <TabsTrigger value="design" data-testid="tab-design">🎨 التصميم</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">🖼️ الشعار/الختم</TabsTrigger>
          <TabsTrigger value="numbering" data-testid="tab-numbering">🔢 الترقيم</TabsTrigger>
          <TabsTrigger value="advanced" data-testid="tab-advanced">⚙️ خيارات</TabsTrigger>
        </TabsList>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
          {/* Settings tabs (left/main) */}
          <div className="lg:col-span-3 space-y-4">

        {/* IDENTITY */}
        <TabsContent value="identity">
          <Card>
            <CardHeader><CardTitle>معلومات الوكالة</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>اسم الوكالة (عربي)</Label>
                <Input value={settings.agency_name_ar || ''} onChange={(e) => update({ agency_name_ar: e.target.value })} />
              </div>
              <div>
                <Label>اسم الوكالة (Français)</Label>
                <Input value={settings.agency_name_fr || ''} onChange={(e) => update({ agency_name_fr: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>العنوان</Label>
                <Input value={settings.address || ''} onChange={(e) => update({ address: e.target.value })} />
              </div>
              <div>
                <Label>الهاتف</Label>
                <Input value={settings.phone || ''} onChange={(e) => update({ phone: e.target.value })} />
              </div>
              <div>
                <Label>البريد الإلكتروني</Label>
                <Input value={settings.email || ''} onChange={(e) => update({ email: e.target.value })} />
              </div>
              <div>
                <Label>الموقع الإلكتروني</Label>
                <Input value={settings.website || ''} onChange={(e) => update({ website: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>ملاحظة الفوتر (عربي)</Label>
                <Textarea
                  value={settings.footer_note_ar || ''}
                  onChange={(e) => update({ footer_note_ar: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="md:col-span-2">
                <Label>ملاحظة الفوتر (Français)</Label>
                <Textarea
                  value={settings.footer_note_fr || ''}
                  onChange={(e) => update({ footer_note_fr: e.target.value })}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DESIGN */}
        <TabsContent value="design" className="mt-4">
          <Card>
            <CardHeader><CardTitle>الألوان والخطوط</CardTitle></CardHeader>
            <CardContent>
              <ColorRow label="اللون الأساسي" value={settings.primary_color} onChange={(v) => update({ primary_color: v })} testId="color-primary" />
              <ColorRow label="اللون الثانوي" value={settings.secondary_color} onChange={(v) => update({ secondary_color: v })} testId="color-secondary" />
              <ColorRow label="خلفية الهيدر" value={settings.header_bg} onChange={(v) => update({ header_bg: v })} testId="color-header-bg" />
              <ColorRow label="نص الهيدر" value={settings.header_text} onChange={(v) => update({ header_text: v })} testId="color-header-text" />
              <ColorRow label="خلفية رأس الجدول" value={settings.table_header_bg} onChange={(v) => update({ table_header_bg: v })} testId="color-table-header-bg" />
              <ColorRow label="نص رأس الجدول" value={settings.table_header_text} onChange={(v) => update({ table_header_text: v })} testId="color-table-header-text" />
              <ColorRow label="لون النص" value={settings.text_color} onChange={(v) => update({ text_color: v })} testId="color-text" />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div>
                  <Label>نوع الخط</Label>
                  <Select value={settings.font_family} onValueChange={(v) => update({ font_family: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cairo">Cairo</SelectItem>
                      <SelectItem value="Tajawal">Tajawal</SelectItem>
                      <SelectItem value="DejaVu Sans">DejaVu Sans</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>حجم الخط (pt)</Label>
                  <Input type="number" min={8} max={16}
                    value={settings.font_size_pt || 11}
                    onChange={(e) => update({ font_size_pt: parseInt(e.target.value || '11') })} />
                </div>
                <div>
                  <Label>القالب الافتراضي</Label>
                  <Select value={settings.default_template} onValueChange={(v) => update({ default_template: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="premium_gold">⭐ Premium Gold (الفاخر الذهبي - أسود وذهبي)</SelectItem>
                      <SelectItem value="modern_luxury">✨ Modern Luxury (الذهبي الفاخر)</SelectItem>
                      <SelectItem value="luxury_pro">👑 Luxury Pro (الفاخر المتقدم)</SelectItem>
                      <SelectItem value="classic_pro">📜 Classic Pro (كلاسيكي محترف)</SelectItem>
                      <SelectItem value="minimal">🤍 Minimal (بسيط نظيف)</SelectItem>
                      <SelectItem value="classic">📋 Classic ReportLab (التصميم القديم)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ASSETS (Logo / Stamp / Signature) */}
        <TabsContent value="assets" className="mt-4">
          <Card>
            <CardHeader><CardTitle>الشعار والختم والتوقيع</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AssetUploader
                kind="logo"
                label="الشعار (Logo)"
                currentUrl={settings.logo_url}
                onUploaded={(url) => update({ logo_url: url })}
                testId="asset-logo"
              />
              <AssetUploader
                kind="stamp"
                label="الختم (Stamp)"
                currentUrl={settings.stamp_url}
                onUploaded={(url) => update({ stamp_url: url })}
                testId="asset-stamp"
              />
              <AssetUploader
                kind="signature"
                label="التوقيع (Signature)"
                currentUrl={settings.signature_url}
                onUploaded={(url) => update({ signature_url: url })}
                testId="asset-signature"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* NUMBERING */}
        <TabsContent value="numbering" className="mt-4">
          <Card>
            <CardHeader><CardTitle>إعدادات الترقيم</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>بادئة الفواتير</Label>
                <Input value={settings.invoice_prefix || ''} onChange={(e) => update({ invoice_prefix: e.target.value })} />
              </div>
              <div>
                <Label>بادئة العمرة</Label>
                <Input value={settings.umrah_prefix || ''} onChange={(e) => update({ umrah_prefix: e.target.value })} />
              </div>
              <div>
                <Label>بادئة التأشيرات</Label>
                <Input value={settings.visa_prefix || ''} onChange={(e) => update({ visa_prefix: e.target.value })} />
              </div>
              <div>
                <Label>دورة إعادة الترقيم</Label>
                <Select value={settings.reset_period} onValueChange={(v) => update({ reset_period: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">سنوياً</SelectItem>
                    <SelectItem value="monthly">شهرياً</SelectItem>
                    <SelectItem value="none">بدون إعادة ترقيم</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ADVANCED */}
        <TabsContent value="advanced" className="mt-4">
          <Card>
            <CardHeader><CardTitle>الخيارات المتقدمة</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between border rounded-lg p-3">
                <Label>إظهار العلامة المائية (Watermark)</Label>
                <Switch checked={!!settings.show_watermark} onCheckedChange={(v) => update({ show_watermark: v })} />
              </div>
              <div className="flex items-center justify-between border rounded-lg p-3">
                <Label>إظهار QR Code للتحقق</Label>
                <Switch checked={!!settings.show_qr} onCheckedChange={(v) => update({ show_qr: v })} />
              </div>
              <div className="flex items-center justify-between border rounded-lg p-3">
                <Label>إظهار الختم</Label>
                <Switch checked={!!settings.show_stamp} onCheckedChange={(v) => update({ show_stamp: v })} />
              </div>
              <div className="flex items-center justify-between border rounded-lg p-3">
                <Label>إظهار التوقيع</Label>
                <Switch checked={!!settings.show_signature} onCheckedChange={(v) => update({ show_signature: v })} />
              </div>
              <div className="flex items-center justify-between border rounded-lg p-3">
                <Label>إظهار اسم المُنشئ (الموظف)</Label>
                <Switch checked={!!settings.show_creator_name} onCheckedChange={(v) => update({ show_creator_name: v })} />
              </div>
              <div className="flex items-center justify-between border rounded-lg p-3">
                <Label>إظهار اسم الفرع</Label>
                <Switch checked={!!settings.show_branch_name} onCheckedChange={(v) => update({ show_branch_name: v })} />
              </div>

              <div>
                <Label>حجم الورق</Label>
                <Select value={settings.paper_size} onValueChange={(v) => update({ paper_size: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A4">A4</SelectItem>
                    <SelectItem value="A5">A5</SelectItem>
                    <SelectItem value="thermal">حراري (Thermal)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>اتجاه الورق</Label>
                <Select value={settings.orientation} onValueChange={(v) => update({ orientation: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">عمودي</SelectItem>
                    <SelectItem value="landscape">أفقي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>الهامش (mm)</Label>
                <Input
                  type="number" min={5} max={30}
                  value={settings.margin_mm || 14}
                  onChange={(e) => update({ margin_mm: parseInt(e.target.value || '14') })}
                />
              </div>
              <div>
                <Label>رمز العملة</Label>
                <Input value={settings.currency_symbol || ''} onChange={(e) => update({ currency_symbol: e.target.value })} />
              </div>
              <div>
                <Label>تنسيق التاريخ</Label>
                <Input value={settings.date_format || ''} onChange={(e) => update({ date_format: e.target.value })} placeholder="%d/%m/%Y" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

          </div>

          {/* LIVE PREVIEW PANEL (right column) */}
          <div className="lg:col-span-2">
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">👁️ معاينة حيّة</CardTitle>
                  <label className="flex items-center gap-2 text-xs text-noir-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={livePreview}
                      onChange={(e) => setLivePreview(e.target.checked)}
                      data-testid="live-preview-toggle"
                    />
                    تحديث تلقائي
                  </label>
                </div>
                <p className="text-xs text-noir-500 mt-1">
                  {livePreview
                    ? '⚡ التغييرات تظهر هنا فوراً (بعد 0.8 ث)'
                    : '⏸️ تحديث يدوي فقط — اضغط 💾 حفظ'}
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {previewUrl ? (
                  <object
                    key={previewBust}
                    data={previewUrl}
                    type="application/pdf"
                    className="w-full bg-noir-100 rounded-b-xl"
                    style={{ height: '78vh', border: 'none' }}
                    data-testid="live-preview-iframe"
                  >
                    <div className="text-center text-noir-500 p-12">
                      المتصفح لا يدعم عرض PDF داخل الصفحة.
                      <br />
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-600 underline"
                      >
                        افتح المعاينة في تبويب جديد
                      </a>
                    </div>
                  </object>
                ) : (
                  <div className="text-center text-noir-500 p-12">
                    لا توجد عملية يومية لمعاينتها.
                    <br />
                    أضف عملية واحدة على الأقل لرؤية المعاينة.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Tabs>
    </div>
  );
};

export default PDFSettingsManagement;
